import React, { useEffect, useState } from 'react';
import { Bell, AlertCircle, CreditCard, Trash2, Check, Loader2, PartyPopper } from 'lucide-react';
import { useApp } from "@/contexts/AppContext";
import { useTranslation } from 'react-i18next';
import notificationService, { type AppNotification } from '@/api/services/notificationService';

type NotificationTab = 'all' | 'unread' | 'read';

export default function NotificationPanel() {
  const { theme } = useApp();
  const { i18n } = useTranslation();
  const [langOverride, setLangOverride] = useState(
    localStorage.getItem('i18nextLng')?.startsWith('fr') ? 'fr' : 'en'
  );
  const t = i18n.getFixedT(langOverride);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [activeTab, setActiveTab] = useState<NotificationTab>('all');
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const [serverUnreadCount, setServerUnreadCount] = useState(0);

  const broadcastUnreadCount = (count: number) => {
    window.dispatchEvent(new CustomEvent('notificationsUnreadCountChanged', { detail: count }));
  };

  const fetchNotifications = async (nextPage = page) => {
    try {
      setLoading(true);
      setError('');
      const response = await notificationService.getNotifications({
        page: nextPage,
        limit,
      });
      setNotifications(response.notifications);
      setTotalPages(response.pagination.pages || 1);
      setTotalNotifications(response.pagination.total || response.notifications.length);
      const unreadCount = response.unreadCount || 0;
      setServerUnreadCount(unreadCount);
      broadcastUnreadCount(unreadCount);
      setPage(response.pagination.page || nextPage);
    } catch (fetchError: unknown) {
      const err = fetchError as { response?: { data?: { message?: string } }, message?: string };
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t('enduser.notification.error.loadFailed', {
            defaultValue: 'Failed to load notifications.',
          })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchNotifications(1);
  }, []);

  useEffect(() => {
    const storedLang = localStorage.getItem('i18nextLng');
    if (storedLang) {
      const normalized = storedLang.startsWith('fr') ? 'fr' : 'en';
      setLangOverride(normalized);
      if (i18n.language !== normalized) {
        i18n.changeLanguage(normalized);
      }
    }
    const onAppLanguageChanged = (event: Event) => {
      const next = (event as CustomEvent).detail as string;
      const normalized = next?.startsWith('fr') ? 'fr' : 'en';
      setLangOverride(normalized);
    };
    window.addEventListener('appLanguageChanged', onAppLanguageChanged as EventListener);
    return () => {
      window.removeEventListener('appLanguageChanged', onAppLanguageChanged as EventListener);
    };
  }, [i18n]);

  // KX-36 "the moment worth engineering for": the first time a subscriber
  // connects to a new node. The backend emits type `device_activity` with
  // metadata.event === 'first_connection' (one concept, one source — AGENTS §3),
  // so the celebratory UI keys off the metadata, not a dedicated type enum.
  const isFirstConnection = (notification: AppNotification): boolean =>
    notification.metadata?.event === 'first_connection';

  const getIcon = (type: string, notification?: AppNotification) => {
    if (notification && isFirstConnection(notification)) {
      return <PartyPopper className="w-6 h-6 text-fuchsia-500" />;
    }
    if (type.includes('payment')) {
      return <CreditCard className="w-6 h-6 text-green-500" />;
    }
    if (type.includes('alert')) {
      return <AlertCircle className="w-6 h-6 text-orange-500" />;
    }
    return <Bell className="w-6 h-6 text-blue-500" />;
  };

  const formatNotificationDate = (createdAt: string) => {
    try {
      return new Date(createdAt).toLocaleString(langOverride === 'fr' ? 'fr-FR' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return createdAt;
    }
  };

  const getNotificationTitle = (notification: AppNotification) => {
    return notification.title || t('enduser.notification.types.default.title', {
      defaultValue: 'Notification',
    });
  };

  const getNotificationMessage = (notification: AppNotification) => {
    return notification.message || t('enduser.notification.types.default.message', {
      defaultValue: 'You have a new notification.',
    });
  };

  const filteredNotifications = notifications.filter((notification) => {
    if (activeTab === 'unread') return !notification.read;
    if (activeTab === 'read') return notification.read;
    return true;
  });

  const handleMarkAsRead = async (id: string) => {
    try {
      setActionLoadingId(id);
      await notificationService.markAsRead(id);
      setNotifications((current) =>
        current.map((notification) =>
          notification._id === id
            ? {
                ...notification,
                read: true,
                readAt: new Date().toISOString(),
              }
            : notification
        )
      );
      setServerUnreadCount((current) => {
        const nextCount = Math.max(0, current - 1);
        broadcastUnreadCount(nextCount);
        return nextCount;
      });
    } catch (markError: unknown) {
      const err = markError as { response?: { data?: { message?: string } }, message?: string };
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t('enduser.notification.error.markFailed', {
            defaultValue: 'Failed to mark notification as read.',
          })
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      setMarkingAllRead(true);
      await notificationService.markAllAsRead();
      setNotifications((current) =>
        current.map((notification) => ({
          ...notification,
          read: true,
          readAt: notification.readAt || new Date().toISOString(),
        }))
      );
      setServerUnreadCount(0);
      broadcastUnreadCount(0);
    } catch (markError: unknown) {
      const err = markError as { response?: { data?: { message?: string } }, message?: string };
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t('enduser.notification.error.markAllFailed', {
            defaultValue: 'Failed to mark all notifications as read.',
          })
      );
    } finally {
      setMarkingAllRead(false);
    }
  };

  const handleDeleteNotification = async (id: string) => {
    try {
      setActionLoadingId(id);
      const target = notifications.find((notification) => notification._id === id);
      await notificationService.deleteNotification(id);
      setNotifications((current) => current.filter((notification) => notification._id !== id));
      setTotalNotifications((current) => Math.max(0, current - 1));
      if (target && !target.read) {
        setServerUnreadCount((current) => {
          const nextCount = Math.max(0, current - 1);
          broadcastUnreadCount(nextCount);
          return nextCount;
        });
      }
      if (notifications.length === 1 && page > 1) {
        void fetchNotifications(page - 1);
      }
    } catch (deleteError: unknown) {
      const err = deleteError as { response?: { data?: { message?: string } }, message?: string };
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t('enduser.notification.error.deleteFailed', {
            defaultValue: 'Failed to delete notification.',
          })
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-50'}`}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {t('enduser.notification.headertitle', { defaultValue: 'All Notifications' })}
              </h1>
              <p className={`text-sm mt-2 font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('enduser.notification.body', {
                  defaultValue: 'You have {{count}} unread notification',
                  count: serverUnreadCount,
                })}
                {serverUnreadCount !== 1 ? 's' : ''}
              </p>
            </div>
            <button
              onClick={() => void handleMarkAllAsRead()}
              disabled={serverUnreadCount === 0 || markingAllRead}
              className={`flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold rounded-xl transition-all w-full sm:w-auto ${
                serverUnreadCount === 0 || markingAllRead
                  ? theme === 'dark'
                    ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg'
              }`}
            >
              {markingAllRead ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
              {t('enduser.notification.markbutton', { defaultValue: 'Mark All Read' })}
            </button>
          </div>

          <div className="flex gap-4 sm:gap-8 overflow-x-auto pb-2 sm:pb-0">
            <button
              onClick={() => setActiveTab('all')}
              className={`pb-4 text-base font-bold transition-colors relative ${
                activeTab === 'all'
                  ? theme === 'dark' ? 'text-white' : 'text-gray-900'
                  : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              <div className="flex items-center gap-2">
                {t('enduser.notification.tabs.all', { defaultValue: 'All' })}
                <span className={`px-2.5 py-0.5 text-xs font-bold rounded-full ${
                  theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                }`}>
                  {totalNotifications}
                </span>
              </div>
              {activeTab === 'all' && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600 rounded-t" />
              )}
            </button>

            <button
              onClick={() => setActiveTab('unread')}
              className={`pb-4 text-base font-bold transition-colors relative ${
                activeTab === 'unread'
                  ? theme === 'dark' ? 'text-white' : 'text-gray-900'
                  : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              <div className="flex items-center gap-2">
                {t('enduser.notification.tabs.unread', { defaultValue: 'Unread' })}
                {serverUnreadCount > 0 && (
                  <span className="px-2.5 py-0.5 text-xs font-bold rounded-full bg-blue-600 text-white">
                    {serverUnreadCount}
                  </span>
                )}
              </div>
              {activeTab === 'unread' && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600 rounded-t" />
              )}
            </button>

            <button
              onClick={() => setActiveTab('read')}
              className={`pb-4 text-base font-bold transition-colors relative ${
                activeTab === 'read'
                  ? theme === 'dark' ? 'text-white' : 'text-gray-900'
                  : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              {t('enduser.notification.tabs.read', { defaultValue: 'Read' })}
              {activeTab === 'read' && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600 rounded-t" />
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className={`mb-5 rounded-2xl border px-5 py-4 ${
            theme === 'dark'
              ? 'bg-red-900/20 border-red-800 text-red-300'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            {error}
          </div>
        )}

        {loading ? (
          <div className={`rounded-2xl shadow-lg border-2 ${
            theme === 'dark' ? 'bg-[#333436] border-gray-800' : 'bg-white border-gray-300'
          }`}>
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-10 h-10 animate-spin text-blue-600 mb-4" />
              <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('enduser.notification.loading', { defaultValue: 'Loading notifications...' })}
              </p>
            </div>
          </div>
        ) : filteredNotifications.length > 0 ? (
          <div className="space-y-5">
            {filteredNotifications.map((notification) => (
              <div
                key={notification._id}
                className={`p-4 sm:p-8 rounded-2xl border-2 transition-all shadow-lg ${
                  isFirstConnection(notification)
                    ? theme === 'dark'
                      ? 'bg-gradient-to-br from-fuchsia-900/30 to-[#333436] border-fuchsia-500/60 shadow-fuchsia-600/10'
                      : 'bg-gradient-to-br from-fuchsia-50 to-white border-fuchsia-300 shadow-fuchsia-100'
                    : !notification.read
                      ? theme === 'dark'
                        ? 'bg-[#333436] border-blue-600/50 shadow-blue-600/10'
                        : 'bg-white border-blue-200 shadow-blue-100'
                      : theme === 'dark'
                        ? 'bg-[#333436] border-gray-700 hover:border-gray-600'
                        : 'bg-white border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
                  <div className="flex items-start gap-4 sm:gap-6 flex-1 min-w-0">
                    <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      isFirstConnection(notification)
                        ? (theme === 'dark' ? 'bg-fuchsia-900/40' : 'bg-fuchsia-100')
                        : theme === 'dark' ? 'bg-[#2a2b2d]' : 'bg-gray-50'
                    }`}>
                      {getIcon(notification.type, notification)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                        <h3 className={`text-base sm:text-lg font-bold truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {getNotificationTitle(notification)}
                        </h3>
                        {!notification.read && (
                          <span className="px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-xs font-bold text-white bg-blue-600 rounded-full shadow-lg whitespace-nowrap">
                            {t('enduser.notification.badgeNew', { defaultValue: 'NEW' })}
                          </span>
                        )}
                      </div>
                      <p className={`text-sm sm:text-base mb-3 font-medium leading-relaxed ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-700'
                      }`}>
                        {getNotificationMessage(notification)}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <p className={`text-xs sm:text-sm font-medium ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                          {formatNotificationDate(notification.createdAt)}
                        </p>
                        <span className={`px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-semibold ${
                          notification.priority === 'high'
                            ? 'bg-red-100 text-red-700'
                            : notification.priority === 'medium'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-emerald-100 text-emerald-700'
                        }`}>
                          {notification.priority}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className={`flex items-center justify-end gap-2 sm:gap-3 w-full sm:w-auto sm:flex-shrink-0 mt-1 sm:mt-0 pt-3 sm:pt-0 border-t sm:border-t-0 ${theme === 'dark' ? 'border-gray-800' : 'border-gray-100'}`}>
                    {!notification.read && (
                      <button
                        onClick={() => void handleMarkAsRead(notification._id)}
                        disabled={actionLoadingId === notification._id}
                        className={`px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-bold rounded-xl transition-all ${
                          theme === 'dark'
                            ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white'
                            : 'bg-gray-200 hover:bg-gray-300 text-gray-700 hover:text-gray-900'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {actionLoadingId === notification._id
                          ? t('enduser.notification.actions.processing', { defaultValue: 'Processing...' })
                          : t('enduser.notification.actions.markAsRead', { defaultValue: 'Mark as read' })}
                      </button>
                    )}
                    <button
                      onClick={() => void handleDeleteNotification(notification._id)}
                      disabled={actionLoadingId === notification._id}
                      className={`p-2 sm:p-3 rounded-xl transition-all ${
                        theme === 'dark'
                          ? 'hover:bg-red-900/30 text-gray-400 hover:text-red-400'
                          : 'hover:bg-red-50 text-gray-600 hover:text-red-500'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                      aria-label={t('enduser.notification.actions.deleteAria', { defaultValue: 'Delete notification' })}
                    >
                      {actionLoadingId === notification._id ? (
                        <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}

            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('enduser.notification.pagination.page', {
                  defaultValue: 'Page {{page}} of {{pages}}',
                  page,
                  pages: totalPages,
                })}
              </p>
              <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
                <button
                  onClick={() => void fetchNotifications(page - 1)}
                  disabled={page <= 1 || loading}
                  className={`px-4 py-2 flex-1 sm:flex-none justify-center rounded-xl text-sm font-semibold transition-colors ${
                    theme === 'dark'
                      ? 'bg-[#333436] text-gray-200 border border-gray-700'
                      : 'bg-white text-gray-700 border border-gray-300'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {t('enduser.notification.pagination.previous', { defaultValue: 'Previous' })}
                </button>
                <button
                  onClick={() => void fetchNotifications(page + 1)}
                  disabled={page >= totalPages || loading}
                  className="px-4 py-2 flex-1 sm:flex-none justify-center rounded-xl text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('enduser.notification.pagination.next', { defaultValue: 'Next' })}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className={`rounded-2xl shadow-lg border-2 ${
            theme === 'dark' ? 'bg-[#333436] border-gray-800' : 'bg-white border-gray-300'
          }`}>
            <div className="flex flex-col items-center justify-center py-16">
              <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-6 ${
                theme === 'dark' ? 'bg-[#2a2b2d]' : 'bg-gray-100'
              }`}>
                <Bell className={`w-12 h-12 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
              </div>
              <h4 className={`text-xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {t('enduser.notification.empty.title', { defaultValue: 'No Notifications' })}
              </h4>
              <p className={`text-sm text-center max-w-md ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                {activeTab === 'all'
                  ? t('enduser.notification.empty.bodyAll', { defaultValue: "You're all caught up! No notifications to show." })
                  : t(`enduser.notification.empty.body${activeTab === 'read' ? 'Read' : 'Unread'}`, {
                      defaultValue: `No ${activeTab} notifications at the moment.`,
                    })}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
